import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { LoadingPanelService } from "../../shared/loading-panel/loading-panel.service";
import { CurrentUserService } from '../../user/services';
import { ArtifactAssemblyTypes, AssemblyArtifact } from "../models/artifact";
import { DeveloperArtifactService } from "../services/developer-artifact.service";
import { DuplicateCheckService } from "../../tracking/services/duplicate-check.service";
import { DuplicateCheckTypeEntity } from "../../tracking/models/duplicate-check-type";
import { GenericModalService, ModalSettingsMessage } from "../../shared/generic-modal/generic-modal.service";
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { SvgIconComponent } from "../../shared/icons/svg-icon.component";
import { PermissionRequiredDirective } from "../../shared/authentication/permission-required.directive";
import { ChangeLogLinkComponent } from "../../shared/change-log/change-log-link/change-log-link.component";
import { ChangeLogLinkEntityDirective } from "../../shared/change-log/change-log-link-entity/change-log-link-entity.directive";
import { DxDataGridModule } from "devextreme-angular";
import { BizDataGridDirective } from "../../shared/grid/biz-grid.directive";
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from "devextreme-angular/ui/nested";
import { DxTemplateModule } from "devextreme-angular/core";
import { DeveloperArtifactAssemblyPickerComponent } from "../developer-artifacts/developer-artifact-assembly-picker.component";
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { BizDateColumnDirective } from 'src/app/shared/grid/biz-grid-date.directive';
import { BizUserColumnDirective } from 'src/app/shared/grid/biz-grid-user.directive';
import { GridService } from "../../shared/grid/grid.service";
import { DxDataGridTypes } from "devextreme-angular/ui/data-grid";

@Component({
    selector: 'app-developer-duplicate-check-types',
    templateUrl: './developer-duplicate-check-types.component.html',
    styleUrls: ['./developer-duplicate-check-types.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule,
         DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent, BizUserColumnDirective, BizDateColumnDirective]
})
export class DeveloperDuplicateCheckTypesComponent implements OnInit {

    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    hasShowLogPerm = false;

    artifacts: AssemblyArtifact[] = [];
    duplicateCheckTypes: DuplicateCheckTypeEntity[] = [];

    constructor(
        private currentUserService: CurrentUserService,
        private developerArtifactService: DeveloperArtifactService,
        private loadingPanelService: LoadingPanelService,
        private duplicateCheckService: DuplicateCheckService,
        private modalService: GenericModalService,
        private toastService: ToastService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
            this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeDuplicateCheck, ArtifactAssemblyTypes.TypeDuplicateCheck2]);
        this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_DuplicateCheckType_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_DuplicateCheckType_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_DuplicateCheckType_Delete');
        this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.refresh();
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading duplicate checks...');
        this.duplicateCheckTypes = await this.duplicateCheckService.getDuplicateCheckTypes();
        this.loadingPanelService.hideLoadingPanel();
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return "Unknown...";
    }

    async saveDuplicateCheckType(entity: DuplicateCheckTypeEntity): Promise<DuplicateCheckTypeEntity> {
        try {
            const savedItem = await this.duplicateCheckService.saveDuplicateCheckType(entity);
            this.toastService.showToast('Duplicate check saved...', ToastType.success);
            return savedItem;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save duplicate check...', ToastType.error);
            return undefined;
        }
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || null);
        if (cellInfo.data.ArtifactId != artifactId && artifactId) { // Only check artifact if it has changed!
            try {
                this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
                const duplicateCheckArtifactInfo = await this.duplicateCheckService.getDuplicateCheckArtifactInfo(artifactId);
                cellInfo.component.cellValue(cellInfo.row.rowIndex, 'Type', duplicateCheckArtifactInfo.DuplicateCheckType);
                cellInfo.component.cellValue(cellInfo.row.rowIndex, 'DuplicateCheckKey', duplicateCheckArtifactInfo.DuplicateCheckKey);
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<DuplicateCheckTypeEntity>): void {
        e.data = { Id: null, Name: '', Description: '' } as DuplicateCheckTypeEntity;
    }

    onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<DuplicateCheckTypeEntity>): void => {
        const duplicateCheckTypeEntity = e.data;
        const existing = this.duplicateCheckTypes.find(i => i.ArtifactId === duplicateCheckTypeEntity.ArtifactId && i.Id !== duplicateCheckTypeEntity.Id);
            if (existing) {
                e.cancel = true;
                const settings = new ModalSettingsMessage();
                settings.size = 'lg';
                settings.backdrop = 'static';
                // Make sure we are on top with this modal!
                settings.windowClass = 'ngb-modal-on-top';
                settings.backdropClass = 'ngb-modal-on-top-backdrop';
                settings.title = 'Already exist';
                settings.body = 'Another duplicate check already exists for this artifact. Name of the existing duplicate check: ' + existing.Name;
                this.modalService.message(settings);
            } else {
                e.cancel = this.gridService.handleDataGridRow(duplicateCheckTypeEntity, item => this.saveDuplicateCheckType(item));
            }
    };
    
    onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<DuplicateCheckTypeEntity>): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.saveDuplicateCheckType(item), e.newData);
    };

    onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<DuplicateCheckTypeEntity>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.removeDuplicateCheckType(item));
    };

    async removeDuplicateCheckType(item: DuplicateCheckTypeEntity): Promise<DuplicateCheckTypeEntity> {
        await this.duplicateCheckService.deleteDuplicateCheckType(item.Id);
        this.toastService.showToast('Duplicate check deleted...', ToastType.success);
        return item;
    }
}
